import { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Wallet, CheckCircle2, Clock, AlertTriangle, Receipt } from "lucide-react";
import { toast } from "sonner";
import { tableLabel } from "@/lib/tables";

function timeAgo(iso) {
    const now = new Date();
    const t = new Date(iso);
    const diff = Math.floor((now - t) / 1000);
    if (diff < 60) return `${diff} sn`;
    if (diff < 3600) return `${Math.floor(diff / 60)} dk`;
    return `${Math.floor(diff / 3600)} sa`;
}

export default function CashierDashboard() {
    const [orders, setOrders] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [paying, setPaying] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/orders");
            setOrders(
                data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
            );
        } catch (e) {
            toast.error(formatError(e));
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 4000);
        return () => clearInterval(id);
    }, [load]);

    const completeOrder = async (orderId) => {
        if (!window.confirm("Ödeme alındı, sipariş kapatılsın mı?")) return;
        setPaying(true);
        try {
            await api.patch(`/orders/${orderId}/complete`);
            toast.success("Ödeme alındı, sipariş kapatıldı");
            setSelectedId(null);
            load();
        } catch (e) {
            toast.error(formatError(e));
        } finally {
            setPaying(false);
        }
    };

    const totalRevenue = orders.reduce(
        (s, o) => s + o.items.reduce((s2, i) => s2 + i.price * i.qty, 0),
        0
    );

    const selected = orders.find((o) => o.id === selectedId);
    const selectedTotal = selected
        ? selected.items.reduce((s, i) => s + i.price * i.qty, 0)
        : 0;
    const selectedAllSent = selected
        ? selected.items.length > 0 && selected.items.every((i) => i.status === "ready")
        : false;

    return (
        <div className="min-h-screen bg-[#FAF9F6]">
            <AppHeader
                title="Kasa"
                right={
                    <div className="hidden md:flex items-center gap-2 text-sm text-white px-4 py-2 rounded-lg bg-[#6B8E7B]" data-testid="open-tab-total">
                        <Wallet className="w-4 h-4" />
                        Açık hesap: ₺{totalRevenue.toFixed(2)}
                    </div>
                }
            />
            <main className="max-w-[1600px] mx-auto px-6 md:px-8 py-8">
                <section>
                    <div className="mb-6">
                        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#59605B] mb-2">Ödeme Bekleyen</div>
                        <h1 className="font-display text-3xl sm:text-4xl font-semibold text-[#2C302E]">
                            Açık masalar
                        </h1>
                        <p className="text-sm text-[#59605B] mt-2">
                            Ödemeyi nakit/kart olarak aldıktan sonra "Ödeme Alındı" butonuna basın.
                        </p>
                    </div>

                    {orders.length === 0 ? (
                        <div className="bg-white border border-dashed border-[#E5E3D8] rounded-2xl p-16 text-center" data-testid="cashier-empty">
                            <CheckCircle2 className="w-12 h-12 text-[#6B8E7B] mx-auto mb-4" />
                            <div className="font-display text-xl text-[#2C302E]">Açık hesap yok</div>
                            <div className="text-sm text-[#59605B] mt-2">Tüm masalar kapalı. Hayırlı kazançlar.</div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3">
                            {orders.map((order) => {
                                const total = order.items.reduce((s, i) => s + i.price * i.qty, 0);
                                const sentCount = order.items.filter((i) => i.status === "ready").length;
                                const allSent = sentCount === order.items.length && order.items.length > 0;
                                const active = selectedId === order.id;
                                return (
                                    <button
                                        key={order.id}
                                        onClick={() => setSelectedId(order.id)}
                                        data-testid={`cashier-order-${order.id}`}
                                        className={`text-center bg-white border rounded-2xl p-3 aspect-square flex flex-col justify-between transition-all hover:-translate-y-0.5 hover:shadow-md ${
                                            active
                                                ? "border-[#D96C4A] ring-4 ring-[#D96C4A]/15 shadow-md"
                                                : allSent
                                                ? "border-[#6B8E7B]/40"
                                                : "border-[#E3B505]/40"
                                        }`}
                                    >
                                        <div>
                                            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#59605B] leading-none">
                                                {tableLabel(order.table_number).split(" ").slice(0, -1).join(" ")}
                                            </div>
                                            <div
                                                className="font-display text-4xl font-bold leading-tight mt-0.5"
                                                style={{ color: allSent ? "#6B8E7B" : "#2C302E" }}
                                            >
                                                {tableLabel(order.table_number).split(" ").slice(-1)[0]}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="font-display text-lg font-semibold text-[#D96C4A]">₺{total.toFixed(2)}</div>
                                            <div className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${allSent ? "text-[#6B8E7B]" : "text-[#8B6914]"}`}>
                                                {allSent ? "✓ Tümü gitti" : `${sentCount}/${order.items.length} gitti`}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>

                {selected && (
                    <div
                        className="fixed inset-0 z-50 bg-[#2C302E]/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-4"
                        onClick={() => setSelectedId(null)}
                    >
                        <div
                            className="bg-white border border-[#E5E3D8] rounded-2xl shadow-2xl overflow-hidden fade-up w-full max-w-lg max-h-[92vh] flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="bg-[#2C302E] text-white px-6 py-5 flex items-start justify-between">
                                <div>
                                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] opacity-70 mb-1">
                                        <Receipt className="w-3.5 h-3.5" />
                                        Adisyon
                                    </div>
                                    <div className="font-display text-2xl font-semibold">
                                        {tableLabel(selected.table_number)}
                                    </div>
                                    <div className="text-xs opacity-70 mt-1">
                                        Garson: {selected.waiter_username} · {timeAgo(selected.created_at)} önce
                                    </div>
                                </div>
                                <button
                                    onClick={() => setSelectedId(null)}
                                    data-testid="close-bill"
                                    className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/10"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="px-6 py-5 max-h-[420px] overflow-y-auto space-y-3">
                                {selected.items.map((item) => {
                                    const sent = item.status === "ready";
                                    return (
                                        <div
                                            key={item.id}
                                            data-testid={`bill-item-${item.id}`}
                                            className="flex items-start justify-between gap-3 pb-3 border-b border-dashed border-[#E5E3D8] last:border-0 last:pb-0"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm text-[#2C302E]">
                                                    <span className="font-semibold">{item.qty}×</span> {item.name}
                                                </div>
                                                {item.notes && (
                                                    <div className="text-xs text-[#59605B] mt-0.5">Not: {item.notes}</div>
                                                )}
                                                <div className={`text-[10px] mt-1 font-semibold uppercase tracking-wider ${
                                                    sent ? "text-[#6B8E7B]" : "text-[#E3B505]"
                                                }`}>
                                                    {sent ? "✓ Gönderildi" : "⏳ Hazırlanıyor"}
                                                </div>
                                                {item.added_by === "mutfak" && (
                                                    <div className="text-[10px] mt-0.5 font-bold uppercase tracking-wider text-[#D96C4A]">
                                                        Mutfaktan eklendi
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-sm font-medium text-[#2C302E] tabular-nums">
                                                ₺{(item.price * item.qty).toFixed(2)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="px-6 py-5 border-t border-[#E5E3D8] bg-[#FAF9F6]">
                                <div className="flex items-baseline justify-between mb-4">
                                    <span className="text-sm font-medium text-[#59605B]">Toplam</span>
                                    <span className="font-display text-3xl font-semibold text-[#2C302E]" data-testid="bill-total">
                                        ₺{selectedTotal.toFixed(2)}
                                    </span>
                                </div>
                                {!selectedAllSent && (
                                    <div className="flex items-start gap-2 p-3 mb-4 bg-[#E3B505]/10 border border-[#E3B505]/30 rounded-lg text-xs text-[#8B6914]">
                                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                        Bu siparişte mutfaktan henüz gitmemiş yemekler var. Yine de ödeme alabilirsiniz, karar size ait.
                                    </div>
                                )}
                                <button
                                    onClick={() => completeOrder(selected.id)}
                                    disabled={paying}
                                    data-testid="payment-received"
                                    className="w-full bg-[#6B8E7B] text-white font-medium px-6 py-4 rounded-xl hover:bg-[#567564] active:scale-[0.99] transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-md"
                                >
                                    <Wallet className="w-5 h-5" />
                                    {paying ? "İşleniyor..." : "Ödeme Alındı · Siparişi Kapat"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}
